/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    IconButton,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {FileIcon} from '../icons'
import {useSiteContext} from '../../contexts/site'
import {KsLogo} from '../icons'
import {getUrl} from '../../utils/cloudinary-utils'

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

export const reportErrorBoundaryToTelemetry = (message, stack, status) => {
    // Only report client-side React rendering crashes caught by AppErrorBoundary.getDerivedStateFromError
    // (which have no HTTP status), excluding 404/unroutable requests and getProps() HTTP errors.
    if (typeof window === 'undefined' || status != null || (!message && !stack)) return
    try {
        const summary = String(message || stack).slice(0, 255)
        const pageEvent = `AppErrorBoundary_Error: ${summary}`.slice(0, 255)
        window._uxa = window._uxa || []
        if (typeof window._uxa.push === 'function') {
            window._uxa.push([
                'trackDynamicVariable',
                {key: 'AppErrorBoundary_Message', value: summary}
            ])
            window._uxa.push(['trackPageEvent', pageEvent])
        }
        console.error('[AppErrorBoundary]', {message, status, stack})
    } catch {
        // Telemetry reporting inside Error boundary must never throw
    }
}

const Error = (props) => {
    const {message, stack, status} = props
    const {isYR} = useSiteContext()
    const styles = useMultiStyleConfig('Header')
    const title = "This page isn't working"
    const basket = useCurrentBasket()
    // Logic to show maintenance page
    useEffect(() => {
        // Show Maintenance Page for 503 (Forbidden) responses.
        if (basket?.failureReason?.response?.headers?.get('sfdc_maintenance')) {
            window.location.href = window.location.origin + '/maintenance'
        }
    }, [basket?.status])

    useEffect(() => {
        reportErrorBoundaryToTelemetry(message, stack, status)
    }, [message, stack, status])

    return (
        <Flex id="sf-app" flex={1} direction="column" minWidth={'375px'}>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Box as="header" width="full" boxShadow="base" backgroundColor="white">
                <Box
                    maxWidth="container.xxxl"
                    marginLeft="auto"
                    marginRight="auto"
                    px={[4, 4, 6, 8]}
                    paddingTop={[1, 1, 2, 4]}
                    paddingBottom={[3, 3, 2, 4]}
                >
                    <IconButton
                        aria-label="logo"
                        icon={
                            isYR ? (
                                <Image
                                    {...styles.logo}
                                    alt="Yellow Rose Logo"
                                    src={getUrl('UIImages/images/small-yr-logo.svg')}
                                />
                            ) : (
                                <KsLogo {...styles.logo} alt="Kendra Scott Logo" />
                            )
                        }
                        marginBottom={[1, 1, 2, 0]}
                        variant="unstyled"
                        // We need to use window.location.href here rather than history
                        // as the application is in an error state. We need to force a
                        // hard navigation to get back to the normal state.
                        onClick={() => (window.location.href = '/')}
                    />
                </Box>
            </Box>
            <Box
                as="main"
                id="app-main"
                role="main"
                layerStyle="page"
                padding={{lg: 8, md: 6, sm: 0, base: 0}}
                flex={1}
            >
                <Flex
                    direction={'column'}
                    justify="center"
                    px={{base: 4, md: 6, lg: 50}}
                    py={{base: 20, md: 24}}
                >
                    <Flex align="center" direction="column">
                        <FileIcon boxSize={['30px', '32px']} mb={8} />
                        <Heading as="h2" fontSize={['xl', '2xl', '2xl', '3xl']} mb={2}>
                            {title}
                        </Heading>
                        <Box maxWidth="440px" marginBottom={8}>
                            <Text align="center">
                                An error has occurred. Try refreshing the page or if you need
                                immediate help please contact support.
                            </Text>
                            {message && (
                                <Box
                                    as="pre"
                                    mt={4}
                                    fontSize="sm"
                                    background="gray.50"
                                    borderColor="gray.200"
                                    borderStyle="solid"
                                    borderWidth="1px"
                                    overflow="auto"
                                    padding={4}
                                >
                                    {message}
                                </Box>
                            )}
                        </Box>
                        <Stack direction={['column', 'row']} spacing={4} width={['100%', 'auto']}>
                            <Button
                                variant="outline"
                                bg="white"
                                as="a"
                                borderRadius="90px"
                                borderColor="black.100"
                                target="_blank"
                                href="https://pavunari.com/customer-service.html"
                            >
                                Contact Support
                            </Button>
                            <Button onClick={() => window.location.reload()}>
                                Refresh the page
                            </Button>
                        </Stack>
                    </Flex>
                    {stack && (
                        <Box marginTop={20}>
                            <Text fontWeight="bold" fontSize="md">
                                Stack Trace
                            </Text>
                            <Box
                                as="pre"
                                mt={4}
                                fontSize="sm"
                                background="gray.50"
                                borderColor="gray.200"
                                borderStyle="solid"
                                borderWidth="1px"
                                overflow="auto"
                                padding={4}
                            >
                                {stack}
                            </Box>
                        </Box>
                    )}
                </Flex>
            </Box>
        </Flex>
    )
}

Error.propTypes = {
    // JavaScript error stack trace: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
    stack: PropTypes.string,
    // HTTP status code: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
    status: PropTypes.number,
    // A description of the error, if available
    message: PropTypes.string
}

export default Error
